import { Avatar, FlexBox, Text } from '@dixa/lib-shared-habla';
import type { SearchUser } from 'lib-search-types';
import { User } from 'lib-shared-types-api';
import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { HookFormSearchSelect } from 'components/hookForm';
import { getUserDisplayName } from 'utils/user';

export type SelectContactProps = {
  /** The target contact that are being merged into */
  targetContact: User;
  /** When a contact is selected */
  onContactChange: (contact: SearchUser) => void;
};

/** Select contact is a contact selection component */
export const SelectContact = ({
  targetContact,
  onContactChange,
}: SelectContactProps): JSX.Element => {
  const targetContactDisplayName = getUserDisplayName(targetContact);

  const { control } = useForm<{ contact: SearchUser[] }>({
    defaultValues: {
      contact: [],
    },
  });

  const contactValue = useWatch({ control, name: 'contact' });

  useEffect(() => {
    if (contactValue?.[0]) {
      onContactChange(contactValue[0]);
    }
  }, [contactValue, onContactChange]);

  return (
    <FlexBox flexDirection="column" gap="300">
      <Text.Paragraph>
        Select which contact you want to merge with{' '}
        <strong>{targetContactDisplayName}</strong>.
      </Text.Paragraph>

      <HookFormSearchSelect
        control={control}
        name="contact"
        label="Select a contact"
        scope="users"
        initialArg={{
          filters: {
            strategy: 'all',
            filters: [
              {
                field: 'user_type',
                operator: 'is_one',
                values: [{ value: 'contact' }],
              },
            ],
          },
          sortBy: { field: 'display_name', order: 'asc' },
          pageSize: 20,
        }}
        componentProps={{
          placeholder: 'Search after name, phone number or email',
          equalityFn: (a, b) => a?.id === b?.id,
          getItemTitleByValue: (user) => (user ? getUserDisplayName(user) : ''),
        }}
        renderSelectionListItemContent={(searchUser) => {
          const name = getUserDisplayName(searchUser);
          const secondLine = searchUser.email ?? searchUser.phone_number;
          const isNameAndSecondLineEqual = name === secondLine;

          return (
            <FlexBox gap="200" alignItems="center">
              <Avatar user={searchUser} />

              <div>
                <Text.Heading variant="heading-4">{name}</Text.Heading>

                {!isNameAndSecondLineEqual && (
                  <Text.Paragraph variant="4" subdued>
                    <FlexBox gap="200" alignItems="center">
                      {searchUser.phone_number && (
                        <span>{searchUser.phone_number}</span>
                      )}

                      {searchUser.email && <span>{searchUser.email}</span>}
                    </FlexBox>
                  </Text.Paragraph>
                )}
              </div>
            </FlexBox>
          );
        }}
      />
    </FlexBox>
  );
};
